import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Input, message, Tag, Space, Checkbox, Pagination, QRCode, Select } from 'antd';
import { CheckOutlined, CheckSquareOutlined, BorderOutlined, SendOutlined } from '@ant-design/icons';
import axios from 'axios';
import config from '../config';

const { TextArea } = Input;
const { Option } = Select;

const DownloadManager = () => {
  const [downloadList, setDownloadList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chatVisible, setChatVisible] = useState(false);
  const [currentCartId, setCurrentCartId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [qrCodeVisible, setQrCodeVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchDownloadList = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        message.error('请先登录');
        return;
      }

      const response = await axios.get(
        `${config.apiBaseUrl}/api/download-cart/list`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setDownloadList(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      if (error.response) {
        message.error(error.response.data.message || '获取列表失败');
      } else {
        message.error('获取列表失败，请稍后重试');
      }
      setDownloadList([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchChatMessages = async (cartId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        message.error('请先登录');
        return;
      }

      const response = await axios.get(
        `${config.apiBaseUrl}/api/download-chat/list/${cartId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setMessages(response.data);
    } catch (error) {
      if (error.response) {
        message.error(error.response.data.message || '获取聊天记录失败');
      } else {
        message.error('获取聊天记录失败，请稍后重试');
      }
    }
  };

  const handleRemove = async (cartId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        message.error('请先登录');
        return;
      }

      await axios.post(
        `${config.apiBaseUrl}/api/download-cart/remove`,
        { cart_id: cartId },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      message.success('已从列表中移除');
      fetchDownloadList();
      setSelectedRowKeys(prev => prev.filter(key => key !== cartId));
    } catch (error) {
      if (error.response) {
        message.error(error.response.data.message || '移除失败');
      } else {
        message.error('移除失败，请稍后重试');
      }
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) {
      message.error('请输入消息内容');
      return;
    }

    try {
      setSendingMessage(true);
      const token = localStorage.getItem('token');
      if (!token) {
        message.error('请先登录');
        return;
      }

      await axios.post(
        `${config.apiBaseUrl}/api/download-chat/send`,
        {
          cart_id: currentCartId,
          message: newMessage.trim()
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setNewMessage('');
      fetchChatMessages(currentCartId);
    } catch (error) {
      if (error.response) {
        message.error(error.response.data.message || '发送失败');
      } else {
        message.error('发送失败，请稍后重试');
      }
    } finally {
      setSendingMessage(false);
    }
  };

  const handleOpenChat = (cartId) => {
    setCurrentCartId(cartId);
    fetchChatMessages(cartId);
    setChatVisible(true);
  };

  const handleSubmitSelected = async () => {
    if (selectedRowKeys.length === 0) {
      message.error('请至少选择一部电影');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      if (!token) {
        message.error('请先登录');
        return;
      }

      // 发送提交请求
      await axios.post(
        `${config.apiBaseUrl}/api/download-cart/submit`,
        { cart_ids: selectedRowKeys },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      message.success('提交成功，请添加管理员微信');
      setQrCodeVisible(true);
      fetchDownloadList(); // 刷新列表状态
    } catch (error) {
      if (error.response) {
        message.error(error.response.data.message || '提交失败');
      } else {
        message.error('提交失败，请稍后重试');
      }
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    fetchDownloadList();
  }, []);

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => {
      setSelectedRowKeys(selectedKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: record.status !== 'pending', // 只有等待处理的项目可以选择
    }),
    columnWidth: 90,
    columnTitle: <span style={{ fontWeight: '500', color: '#1890ff' }}>选择</span>,
    renderCell: (checked, record, index, originNode) => {
      if (record.status !== 'pending') {
        return null; // 不显示禁用的勾选框
      }
      return (
        <Button 
          type={checked ? 'primary' : 'default'}
          shape="round"
          size="small"
          icon={checked ? <CheckOutlined /> : null}
          style={{ 
            width: '70px',
            height: '32px',
            transition: 'all 0.3s',
            backgroundColor: checked ? '#1890ff' : 'white',
            borderColor: checked ? '#1890ff' : '#d9d9d9',
            color: checked ? 'white' : '#666',
            boxShadow: checked ? '0 2px 6px rgba(24, 144, 255, 0.25)' : 'none',
            fontWeight: checked ? '500' : 'normal',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
          onClick={(e) => {
            e.stopPropagation();
            const newSelectedKeys = [...selectedRowKeys];
            const cartId = record.cart_id;
            if (checked) {
              const index = newSelectedKeys.indexOf(cartId);
              if (index !== -1) {
                newSelectedKeys.splice(index, 1);
              }
            } else {
              newSelectedKeys.push(cartId);
            }
            setSelectedRowKeys(newSelectedKeys);
          }}
        >
          {checked ? '已选' : '选择'}
        </Button>
      );
    }
  };

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleShowSizeChange = (current, size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const paginatedData = downloadList.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const columns = [
    {
      title: <span style={{ fontWeight: '500' }}>电影名称</span>,
      dataIndex: 'name',
      key: 'name',
      width: '30%',
    },
    {
      title: <span style={{ fontWeight: '500' }}>导演</span>,
      dataIndex: 'dir',
      key: 'dir',
      width: '20%',
    },
    {
      title: <span style={{ fontWeight: '500' }}>年份</span>,
      dataIndex: 'year',
      key: 'year',
      width: '10%',
    },
    {
      title: <span style={{ fontWeight: '500' }}>IMDB编号</span>,
      dataIndex: 'imdb',
      key: 'imdb',
      width: '15%',
    },
    {
      title: <span style={{ fontWeight: '500' }}>状态</span>,
      dataIndex: 'status',
      key: 'status',
      width: '10%',
      render: (status) => {
        const statusMap = {
          pending: { color: 'gold', text: '等待处理' },
          processing: { color: 'blue', text: '处理中' },
          completed: { color: 'green', text: '已完成' },
          failed: { color: 'red', text: '失败' }
        };
        const { color, text } = statusMap[status] || { color: 'default', text: status };
        return <Tag color={color}>{text}</Tag>;
      }
    },
    {
      title: <span style={{ fontWeight: '500' }}>操作</span>,
      key: 'action',
      width: '90px',
      render: (_, record) => (
        <Space>
          {record.status === 'pending' && (
            <Button 
              danger
              shape="round"
              size="small"
              style={{ 
                width: '70px',
                height: '32px',
                fontWeight: '500',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                boxShadow: '0 2px 6px rgba(255, 77, 79, 0.15)'
              }}
              onClick={() => handleRemove(record.cart_id)}
            >
              移除
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const selectAllItems = () => {
    const availableItems = paginatedData
      .filter(item => item.status === 'pending')
      .map(item => item.cart_id);
    setSelectedRowKeys(availableItems);
  };

  return (
    <div style={{ padding: '24px' }}>
      <h2>代下载管理</h2>
      
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <div>
          <Button
            type={paginatedData.filter(item => item.status === 'pending').length > 0 && 
                 paginatedData.filter(item => item.status === 'pending').length === 
                 selectedRowKeys.filter(key => paginatedData.some(item => item.cart_id === key)).length ? 'primary' : 'default'}
            onClick={() => {
              if (paginatedData.filter(item => item.status === 'pending').length > 0 && 
                  paginatedData.filter(item => item.status === 'pending').length === 
                  selectedRowKeys.filter(key => paginatedData.some(item => item.cart_id === key)).length) {
                setSelectedRowKeys([]);
              } else {
                selectAllItems();
              }
            }}
            disabled={paginatedData.filter(item => item.status === 'pending').length === 0}
            icon={paginatedData.filter(item => item.status === 'pending').length > 0 && 
                  paginatedData.filter(item => item.status === 'pending').length === 
                  selectedRowKeys.filter(key => paginatedData.some(item => item.cart_id === key)).length ? 
                  <CheckSquareOutlined /> : <BorderOutlined />}
            style={{ marginRight: '8px' }}
          >
            {paginatedData.filter(item => item.status === 'pending').length > 0 && 
             paginatedData.filter(item => item.status === 'pending').length === 
             selectedRowKeys.filter(key => paginatedData.some(item => item.cart_id === key)).length ? 
             '取消全选' : '全选本页'}
          </Button>
          <span style={{ 
            marginLeft: 8, 
            fontSize: '14px',
            fontWeight: selectedRowKeys.length > 0 ? 'bold' : 'normal',
            color: selectedRowKeys.length > 0 ? '#1890ff' : 'inherit'
          }}>
            已选择 <span style={{ fontSize: '16px', color: '#1890ff', fontWeight: 'bold' }}>{selectedRowKeys.length}</span> 项
          </span>
        </div>
        <Button 
          type="primary"
          onClick={handleSubmitSelected}
          disabled={selectedRowKeys.length === 0}
          loading={submitting}
          size="middle"
          icon={<SendOutlined />}
        >
          代下载申请提交
        </Button>
      </div>
      
      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={paginatedData}
        rowKey="cart_id"
        loading={loading}
        pagination={false}
        style={{ 
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)', 
          borderRadius: '8px',
          overflow: 'hidden' 
        }}
      />
      
      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <span style={{ marginRight: 8 }}>共 {downloadList.length} 条记录</span>
          <Select 
            defaultValue={pageSize} 
            style={{ width: 120, marginRight: 16 }} 
            onChange={(value) => {
              setPageSize(value);
              setCurrentPage(1);
            }}
          >
            <Option value={10}>10条/页</Option>
            <Option value={25}>25条/页</Option>
            <Option value={50}>50条/页</Option>
          </Select>
        </div>
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={downloadList.length}
          onChange={handlePageChange}
          showSizeChanger={false}
        />
      </div>

      <Modal
        title="与管理员聊天"
        open={chatVisible}
        onCancel={() => setChatVisible(false)}
        footer={null}
        width={600}
      >
        <div style={{ 
          height: '300px', 
          overflowY: 'auto', 
          border: '1px solid #d9d9d9',
          borderRadius: '4px',
          padding: '10px',
          marginBottom: '16px'
        }}>
          {messages.map(msg => (
            <div
              key={msg.id}
              style={{
                marginBottom: '8px',
                textAlign: msg.is_admin ? 'left' : 'right'
              }}
            >
              <div
                style={{
                  display: 'inline-block',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  backgroundColor: msg.is_admin ? '#f0f0f0' : '#1890ff',
                  color: msg.is_admin ? '#000' : '#fff',
                  maxWidth: '80%'
                }}
              >
                {msg.message}
              </div>
              <div
                style={{
                  fontSize: '12px',
                  color: '#999',
                  marginTop: '4px'
                }}
              >
                {msg.created_at}
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <TextArea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="输入消息..."
            autoSize={{ minRows: 2, maxRows: 4 }}
            style={{ flex: 1 }}
          />
          <Button
            type="primary"
            onClick={handleSendMessage}
            loading={sendingMessage}
            style={{ alignSelf: 'flex-end' }}
          >
            发送
          </Button>
        </div>
      </Modal>

      <Modal
        title="请扫码添加微信"
        open={qrCodeVisible}
        onCancel={() => setQrCodeVisible(false)}
        footer={[
          <Button key="close" onClick={() => setQrCodeVisible(false)}>
            关闭
          </Button>,
          <Button key="check" type="primary" onClick={() => {
            setQrCodeVisible(false);
            message.success('订单已提交，管理员将尽快处理');
          }}>
            已添加微信
          </Button>
        ]}
        width={400}
        centered
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <p>请使用微信扫描以下二维码添加管理员</p>
          <img
            src="/static/images/wechat_qrcode.png"
            alt="微信二维码"
            style={{ margin: '20px auto', width: '200px', height: '200px' }}
          />
          <p style={{ color: '#ff4d4f', marginTop: '10px' }}>添加微信后，请告知管理员您的用户名和下载需求</p>
        </div>
      </Modal>
    </div>
  );
};

export default DownloadManager; 